import React, { useContext, useState, useEffect, useRef } from "react";
import {
    WizardView,
    useWizardPage,
    useSyncedField,
    useRuntimeField,
    FinishPage,
} from "../../components/Wizard";
import classNames from "classnames";
import { valueFromAST } from "graphql";
import { range } from "lodash";
import { param } from "jquery";
import StarekContext from "../../context/StarekContext";

function DeviceTypePage({ inputManifest, emittedManifest }) {
    const starekContext = useContext(StarekContext);
    const { scheme, host, port } =
        starekContext.lastUsedWorkspace?.engine || {};
    const workspace = starekContext.lastUsedWorkspace?.name;
    const authToken = starekContext.auth;

    const [availableModels, setAvailableModels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const hasFetchedRef = useRef(false);

    const [selectedDevice, setSelectedDevice] = useRuntimeField(
        "selected_device",
        null
    );

    const [modelInfo, setModelInfo] = useSyncedField("model", null);

    const deviceModels = availableModels || [];

    useEffect(() => {
        if (hasFetchedRef.current) {
            return;
        }

        const fetchAvailableDevices = async () => {
            if (!scheme || !host || !port || !workspace || !authToken) {
                console.warn("Missing required context for API call");
                setIsLoading(false);
                return;
            }

            hasFetchedRef.current = true;
            const apiUrl = `${scheme}://${host}:${port}/api/v1/${workspace}/devices/available`;

            try {
                setIsLoading(true);
                const response = await fetch(apiUrl, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "Content-Type": "application/json",
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const jsonData = await response.json();
                const models = jsonData?.data || [];

                setAvailableModels(models);

                setError(null);
            } catch (err) {
                console.error("Failed to fetch available device models:", err);
                setError(err.message);
                starekContext.showError?.(
                    `Failed to load available device models: ${err.message}`
                );
            } finally {
                setIsLoading(false);
            }
        };
        fetchAvailableDevices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scheme, host, port, workspace, authToken]);

    const handleModelSelect = (model) => {
        setSelectedDevice(model);
        console.log("Selected model:", model);
        setModelInfo({
            name: model.name,
            image: model.image,
            code: model.code,
        });
    };

    return (
        <div>
            <h3 className="mb-3">Device Type</h3>
            <p className="text-muted mb-4">
                Choose the model of device that this profile can define. The
                model number is printed on the package of the device.
            </p>

            {isLoading && (
                <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "400px" }}
                >
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">
                            Loading available device models...
                        </span>
                    </div>
                </div>
            )}

            {!isLoading && error && deviceModels.length === 0 && (
                <div
                    className="d-flex justify-content-center align-items-center flex-column"
                    style={{ minHeight: "400px" }}
                >
                    <i className="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
                    <p className="text-danger">
                        Failed to load available device models
                    </p>
                    <p className="text-muted">{error}</p>
                </div>
            )}

            {!isLoading && deviceModels.length > 0 && (
                <div className="mb-3">
                    <label className="fw-bold mb-3">
                        Model <span className="text-danger">*</span>
                    </label>
                    <div className="row g-3">
                        {deviceModels.map((model) => (
                            <div key={model.code} className="col-auto">
                                <div
                                    className={classNames(
                                        "card cursor-pointer",
                                        "text-center p-3",
                                        {
                                            "border-primary":
                                                selectedDevice?.code ===
                                                model.code,
                                            "border-3":
                                                selectedDevice?.code ===
                                                model.code,
                                            "bg-light":
                                                selectedDevice?.code ===
                                                model.code,
                                        }
                                    )}
                                    style={{
                                        width: "200px",
                                        cursor: "pointer",
                                        boxShadow:
                                            selectedDevice?.code === model.code
                                                ? "0 4px 6px rgba(0,0,0,0.3)"
                                                : "none",
                                    }}
                                    onClick={() => handleModelSelect(model)}
                                >
                                    <div
                                        className="mb-2"
                                        style={{
                                            height: "140px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            overflow: "hidden",
                                        }}
                                    >
                                        <img
                                            src={
                                                model.image ||
                                                "/path/to/default-device-image.png"
                                            }
                                            alt={model.name}
                                            style={{
                                                maxWidth: "100%",
                                                maxHeight: "100%",
                                                objectFit: "contain",
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = "none";
                                                e.target.nextSibling.style.display =
                                                    "flex";
                                            }}
                                        />
                                        <i
                                            className={classNames(
                                                "fas fa-microchip fa-4x",
                                                {
                                                    "text-primary":
                                                        selectedDevice ===
                                                        model.code,
                                                    "text-muted":
                                                        selectedDevice !==
                                                        model.code,
                                                }
                                            )}
                                            style={{
                                                display: model.image
                                                    ? "none"
                                                    : "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                            aria-hidden="true"
                                        ></i>
                                    </div>
                                    <div className="mb-1">
                                        <div
                                            className={classNames("fw-bold", {
                                                "text-primary":
                                                    selectedDevice ===
                                                    model.code,
                                            })}
                                        >
                                            {model.code.toUpperCase()}
                                        </div>
                                        <div
                                            className={classNames("small", {
                                                "text-primary":
                                                    selectedDevice ===
                                                    model.code,
                                                "text-muted":
                                                    selectedDevice !==
                                                    model.code,
                                            })}
                                        >
                                            {model.name}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function SignalsPage({ inputManifest, emittedManifest, onNextRequested }) {
    const { runtimeVariables } = useWizardPage();

    const [signals, setSignals] = useSyncedField("signals", {});

    // Read shared runtime state (set by DeviceTypePage)
    const selectedDevice = runtimeVariables?.selected_device;
    const signalsByBus = selectedDevice?.downlink?.signals || {};
    const availableBuses = Object.keys(signalsByBus);

    // Page-local state (only needed for UI in this page)
    const [selectedBus, setSelectedBus] = useState(availableBuses[0] || null);

    // Initialize signals with default values from device definition
    useEffect(() => {
        if (signalsByBus && Object.keys(signalsByBus).length > 0) {
            const initialSignals = {};

            Object.keys(signalsByBus).forEach((busType) => {
                const busData = signalsByBus[busType];

                if (Array.isArray(busData)) {
                    // Handle array of signals (gpio, i2c, virt, etc.)
                    initialSignals[busType] = busData.map((signal) => ({
                        name: signal.name,
                        address: signal.address,
                        ...signal.parameters?.reduce((acc, param) => {
                            acc[param.name] = param.default;
                            return acc;
                        }, {}),
                    }));
                } else if (busData.parameters) {
                    // Handle single object with parameters (uart, etc.)
                    initialSignals[busType] = busData.parameters.reduce(
                        (acc, param) => {
                            acc[param.name] = param.default;
                            return acc;
                        },
                        {}
                    );
                }
            });

            setSignals(initialSignals);
        }
    }, [selectedDevice?.code]);

    const handleBusSelect = (bus) => {
        setSelectedBus(bus);
    };

    const handleGpioChannelChange = (channelIndex, field, value) => {
        const updated = [...(signals.gpio || [])];
        updated[channelIndex] = { ...updated[channelIndex], [field]: value };
        setSignals({ ...signals, gpio: updated });
    };

    const handleI2cChannelChange = (channelIndex, field, value) => {
        const updated = [...(signals.i2c || [])];
        updated[channelIndex] = { ...updated[channelIndex], [field]: value };
        setSignals({ ...signals, i2c: updated });
    };

    const handleUartConfigChange = (field, value) => {
        setSignals({
            ...signals,
            uart: { ...(signals.uart || {}), [field]: value },
        });
    };

    const handleVirtChannelChange = (channelIndex, field, value) => {
        const updated = [...(signals.virt || [])];
        updated[channelIndex] = { ...updated[channelIndex], [field]: value };
        setSignals({ ...signals, virt: updated });
    };

    const busLabels = { gpio: "GPIO", uart: "UART", i2c: "I2C", virt: "VIRT" };

    return (
        <div>
            <h3 className="mb-3">Signals</h3>
            <p className="text-muted mb-4">
                Setup the Channels for this Device Profile
            </p>

            <div className="row">
                {/* Left side - Bus selection */}
                <div className="col-md-4">
                    <div className="d-flex flex-column gap-3">
                        {availableBuses.map((bus) => (
                            <div
                                key={bus}
                                className={classNames(
                                    "card p-3 cursor-pointer",
                                    {
                                        "border-success bg-light":
                                            selectedBus === bus,
                                        "border-2": selectedBus === bus,
                                    }
                                )}
                                onClick={() => handleBusSelect(bus)}
                            >
                                <div className="d-flex align-items-center">
                                    <label className="mb-0 fw-bold flex-grow-1">
                                        {bus}
                                    </label>
                                    <i className="fas fa-chevron-right text-muted"></i>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right side - Bus configuration */}
                <div className="col-md-8">
                    {selectedBus === "gpio" && signals.gpio && (
                        <div className="card">
                            <div className="card-body">
                                <p className="text-muted mb-3">
                                    Configure GPIO channels for this device
                                    profile
                                </p>
                                <div className="table-responsive">
                                    <table className="table table-bordered">
                                        <thead className="table-primary">
                                            <tr>
                                                <th>Signal</th>
                                                {signalsByBus.gpio &&
                                                    signalsByBus.gpio.length >
                                                        0 &&
                                                    signalsByBus.gpio[0]
                                                        .parameters &&
                                                    signalsByBus.gpio[0].parameters.map(
                                                        (param) => (
                                                            <th
                                                                key={param.name}
                                                            >
                                                                {param.name}
                                                            </th>
                                                        )
                                                    )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {signals.gpio.map(
                                                (channel, index) => {
                                                    // Get parameter definitions from the signal
                                                    const signal =
                                                        signalsByBus.gpio
                                                            ? signalsByBus.gpio[
                                                                  index
                                                              ]
                                                            : null;
                                                    const params =
                                                        signal?.parameters ||
                                                        [];

                                                    return (
                                                        <tr key={index}>
                                                            <td className="align-middle text-center">
                                                                <div>
                                                                    {
                                                                        channel.name
                                                                    }
                                                                </div>
                                                                <small className="text-muted">
                                                                    {
                                                                        channel.address
                                                                    }
                                                                </small>
                                                            </td>
                                                            {params.map(
                                                                (param) => {
                                                                    const currentValue =
                                                                        channel[
                                                                            param
                                                                                .name
                                                                        ] ||
                                                                        param.default;
                                                                    const isReadonly =
                                                                        param?.readonly ||
                                                                        false;
                                                                    const isDropdown =
                                                                        param.type ===
                                                                        "select";

                                                                    return (
                                                                        <td
                                                                            key={
                                                                                param.name
                                                                            }
                                                                        >
                                                                            {isDropdown ? (
                                                                                <select
                                                                                    className="form-select"
                                                                                    value={
                                                                                        currentValue
                                                                                    }
                                                                                    disabled={
                                                                                        isReadonly
                                                                                    }
                                                                                    onChange={(
                                                                                        e
                                                                                    ) =>
                                                                                        handleGpioChannelChange(
                                                                                            index,
                                                                                            param.name,
                                                                                            e
                                                                                                .target
                                                                                                .value
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    {(
                                                                                        param.range ||
                                                                                        []
                                                                                    ).map(
                                                                                        (
                                                                                            option
                                                                                        ) => (
                                                                                            <option
                                                                                                key={
                                                                                                    option
                                                                                                }
                                                                                                value={
                                                                                                    option
                                                                                                }
                                                                                            >
                                                                                                {
                                                                                                    option
                                                                                                }
                                                                                            </option>
                                                                                        )
                                                                                    )}
                                                                                </select>
                                                                            ) : (
                                                                                <input
                                                                                    type={
                                                                                        param.type ||
                                                                                        "text"
                                                                                    }
                                                                                    className="form-control"
                                                                                    value={
                                                                                        currentValue
                                                                                    }
                                                                                    disabled={
                                                                                        isReadonly
                                                                                    }
                                                                                    onChange={(
                                                                                        e
                                                                                    ) =>
                                                                                        handleGpioChannelChange(
                                                                                            index,
                                                                                            param.name,
                                                                                            e
                                                                                                .target
                                                                                                .value
                                                                                        )
                                                                                    }
                                                                                />
                                                                            )}
                                                                        </td>
                                                                    );
                                                                }
                                                            )}
                                                        </tr>
                                                    );
                                                }
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedBus === "uart" && (
                        <div className="card">
                            <div className="card-body">
                                <p className="text-muted mb-3">
                                    Configure UART settings for this device
                                    profile
                                </p>
                                {signalsByBus.uart &&
                                signalsByBus.uart.parameters &&
                                signalsByBus.uart.parameters.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-bordered">
                                            <thead className="table-primary">
                                                <tr>
                                                    {signalsByBus.uart.parameters.map(
                                                        (param) => (
                                                            <th
                                                                key={param.name}
                                                            >
                                                                {param.name}
                                                            </th>
                                                        )
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    {signalsByBus.uart.parameters.map(
                                                        (param) => {
                                                            const paramKey =
                                                                param.name
                                                                    .replace(
                                                                        /\s+/g,
                                                                        ""
                                                                    )
                                                                    .replace(
                                                                        /^\w/,
                                                                        (c) =>
                                                                            c.toLowerCase()
                                                                    );
                                                            const controlType =
                                                                param.type ||
                                                                "text";
                                                            const isDropdown =
                                                                controlType ===
                                                                "select";
                                                            // Get current value from busConfigurations or use param default
                                                            const currentValue =
                                                                signals.uart &&
                                                                signals.uart[
                                                                    paramKey
                                                                ] !== undefined
                                                                    ? signals
                                                                          .uart[
                                                                          paramKey
                                                                      ]
                                                                    : param.default;

                                                            return (
                                                                <td
                                                                    key={
                                                                        paramKey
                                                                    }
                                                                >
                                                                    {isDropdown ? (
                                                                        <select
                                                                            className="form-select"
                                                                            value={String(
                                                                                currentValue
                                                                            )}
                                                                            onChange={(
                                                                                e
                                                                            ) =>
                                                                                handleUartConfigChange(
                                                                                    paramKey,
                                                                                    isNaN(
                                                                                        e
                                                                                            .target
                                                                                            .value
                                                                                    )
                                                                                        ? e
                                                                                              .target
                                                                                              .value
                                                                                        : Number(
                                                                                              e
                                                                                                  .target
                                                                                                  .value
                                                                                          )
                                                                                )
                                                                            }
                                                                        >
                                                                            {param.range.map(
                                                                                (
                                                                                    option
                                                                                ) => (
                                                                                    <option
                                                                                        key={
                                                                                            option
                                                                                        }
                                                                                        value={
                                                                                            option
                                                                                        }
                                                                                    >
                                                                                        {
                                                                                            option
                                                                                        }
                                                                                    </option>
                                                                                )
                                                                            )}
                                                                        </select>
                                                                    ) : (
                                                                        <input
                                                                            type={
                                                                                controlType ===
                                                                                "number"
                                                                                    ? "number"
                                                                                    : "text"
                                                                            }
                                                                            className="form-control"
                                                                            value={
                                                                                currentValue
                                                                            }
                                                                            onChange={(
                                                                                e
                                                                            ) =>
                                                                                handleUartConfigChange(
                                                                                    paramKey,
                                                                                    isNaN(
                                                                                        e
                                                                                            .target
                                                                                            .value
                                                                                    )
                                                                                        ? e
                                                                                              .target
                                                                                              .value
                                                                                        : Number(
                                                                                              e
                                                                                                  .target
                                                                                                  .value
                                                                                          )
                                                                                )
                                                                            }
                                                                            min={
                                                                                param.range &&
                                                                                !Array.isArray(
                                                                                    param.range
                                                                                ) &&
                                                                                param
                                                                                    .range
                                                                                    .min
                                                                            }
                                                                            max={
                                                                                param.range &&
                                                                                !Array.isArray(
                                                                                    param.range
                                                                                ) &&
                                                                                param
                                                                                    .range
                                                                                    .max
                                                                            }
                                                                        />
                                                                    )}
                                                                </td>
                                                            );
                                                        }
                                                    )}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-muted">
                                        No UART parameters available
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {selectedBus === "i2c" && signals.i2c && (
                        <div className="card">
                            <div className="card-body">
                                <p className="text-muted mb-3">
                                    Configure I2C channels for this device
                                    profile
                                </p>
                                <div className="table-responsive">
                                    <table className="table table-bordered">
                                        <thead className="table-primary">
                                            <tr>
                                                {signalsByBus.i2c &&
                                                    signalsByBus.i2c.length >
                                                        0 &&
                                                    signalsByBus.i2c[0]
                                                        .parameters &&
                                                    signalsByBus.i2c[0].parameters.map(
                                                        (param) => (
                                                            <th
                                                                key={param.name}
                                                            >
                                                                {param.name}
                                                            </th>
                                                        )
                                                    )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {signals.i2c.map(
                                                (channel, index) => (
                                                    <tr key={index}>
                                                        {signalsByBus.i2c &&
                                                            signalsByBus.i2c[0]
                                                                .parameters &&
                                                            signalsByBus.i2c[0].parameters.map(
                                                                (param) => {
                                                                    const paramKey =
                                                                        param.name
                                                                            .replace(
                                                                                /\s+/g,
                                                                                ""
                                                                            )
                                                                            .replace(
                                                                                /^\w/,
                                                                                (
                                                                                    c
                                                                                ) =>
                                                                                    c.toLowerCase()
                                                                            );
                                                                    const controlType =
                                                                        param.type ||
                                                                        "text";
                                                                    const isDropdown =
                                                                        controlType ===
                                                                        "select";
                                                                    const currentValue =
                                                                        channel[
                                                                            paramKey
                                                                        ] !==
                                                                        undefined
                                                                            ? channel[
                                                                                  paramKey
                                                                              ]
                                                                            : param.value;

                                                                    return (
                                                                        <td
                                                                            key={
                                                                                paramKey
                                                                            }
                                                                        >
                                                                            {isDropdown ? (
                                                                                <select
                                                                                    className="form-select"
                                                                                    value={String(
                                                                                        currentValue
                                                                                    )}
                                                                                    onChange={(
                                                                                        e
                                                                                    ) =>
                                                                                        handleI2cChannelChange(
                                                                                            index,
                                                                                            paramKey,
                                                                                            isNaN(
                                                                                                e
                                                                                                    .target
                                                                                                    .value
                                                                                            )
                                                                                                ? e
                                                                                                      .target
                                                                                                      .value
                                                                                                : Number(
                                                                                                      e
                                                                                                          .target
                                                                                                          .value
                                                                                                  )
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    {param.range.map(
                                                                                        (
                                                                                            option
                                                                                        ) => (
                                                                                            <option
                                                                                                key={
                                                                                                    option
                                                                                                }
                                                                                                value={
                                                                                                    option
                                                                                                }
                                                                                            >
                                                                                                {
                                                                                                    option
                                                                                                }
                                                                                            </option>
                                                                                        )
                                                                                    )}
                                                                                </select>
                                                                            ) : (
                                                                                <input
                                                                                    type={
                                                                                        controlType ===
                                                                                        "number"
                                                                                            ? "number"
                                                                                            : "text"
                                                                                    }
                                                                                    className="form-control"
                                                                                    value={
                                                                                        currentValue
                                                                                    }
                                                                                    onChange={(
                                                                                        e
                                                                                    ) =>
                                                                                        handleI2cChannelChange(
                                                                                            index,
                                                                                            paramKey,
                                                                                            isNaN(
                                                                                                e
                                                                                                    .target
                                                                                                    .value
                                                                                            )
                                                                                                ? e
                                                                                                      .target
                                                                                                      .value
                                                                                                : Number(
                                                                                                      e
                                                                                                          .target
                                                                                                          .value
                                                                                                  )
                                                                                        )
                                                                                    }
                                                                                    min={
                                                                                        param.range &&
                                                                                        !Array.isArray(
                                                                                            param.range
                                                                                        ) &&
                                                                                        param
                                                                                            .range
                                                                                            .min
                                                                                    }
                                                                                    max={
                                                                                        param.range &&
                                                                                        !Array.isArray(
                                                                                            param.range
                                                                                        ) &&
                                                                                        param
                                                                                            .range
                                                                                            .max
                                                                                    }
                                                                                />
                                                                            )}
                                                                        </td>
                                                                    );
                                                                }
                                                            )}
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedBus === "virt" && signals.virt && (
                        <div className="card">
                            <div className="card-body">
                                <p className="text-muted mb-3">
                                    Configure Virtual channels for this device
                                    profile
                                </p>
                                <div className="table-responsive">
                                    <table className="table table-bordered">
                                        <thead className="table-primary">
                                            <tr>
                                                <th>Channel</th>
                                                {signalsByBus.virt &&
                                                    signalsByBus.virt.length >
                                                        0 &&
                                                    signalsByBus.virt[0]
                                                        .parameters &&
                                                    signalsByBus.virt[0].parameters.map(
                                                        (param) => (
                                                            <th
                                                                key={param.name}
                                                            >
                                                                {param.name}
                                                            </th>
                                                        )
                                                    )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {signals.virt.map(
                                                (channel, index) => {
                                                    // Get signal definition from signalsByBus
                                                    const signal =
                                                        signalsByBus.virt
                                                            ? signalsByBus.virt[
                                                                  index
                                                              ]
                                                            : null;
                                                    const params =
                                                        signal?.parameters ||
                                                        [];
                                                    const isReadonly =
                                                        signal?.readonly ||
                                                        false;

                                                    return (
                                                        <tr key={index}>
                                                            <td className="align-middle text-center">
                                                                <div>
                                                                    {
                                                                        channel.name
                                                                    }
                                                                </div>
                                                                <small className="text-muted">
                                                                    {
                                                                        channel.address
                                                                    }
                                                                </small>
                                                            </td>
                                                            {params.map(
                                                                (param) => {
                                                                    const paramKey =
                                                                        param.name
                                                                            .replace(
                                                                                /\s+/g,
                                                                                ""
                                                                            )
                                                                            .replace(
                                                                                /^\w/,
                                                                                (
                                                                                    c
                                                                                ) =>
                                                                                    c.toLowerCase()
                                                                            );
                                                                    const currentValue =
                                                                        channel[
                                                                            paramKey
                                                                        ] ||
                                                                        param.default;
                                                                    const paramReadonly =
                                                                        param?.readonly ||
                                                                        isReadonly;

                                                                    return (
                                                                        <td
                                                                            key={
                                                                                paramKey
                                                                            }
                                                                        >
                                                                            {paramReadonly ? (
                                                                                <div className="form-control bg-light">
                                                                                    {
                                                                                        currentValue
                                                                                    }
                                                                                </div>
                                                                            ) : (
                                                                                <select
                                                                                    className="form-select"
                                                                                    value={String(
                                                                                        currentValue
                                                                                    )}
                                                                                    onChange={(
                                                                                        e
                                                                                    ) =>
                                                                                        handleVirtChannelChange(
                                                                                            index,
                                                                                            paramKey,
                                                                                            isNaN(
                                                                                                e
                                                                                                    .target
                                                                                                    .value
                                                                                            )
                                                                                                ? e
                                                                                                      .target
                                                                                                      .value
                                                                                                : Number(
                                                                                                      e
                                                                                                          .target
                                                                                                          .value
                                                                                                  )
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    {param.range.map(
                                                                                        (
                                                                                            option
                                                                                        ) => (
                                                                                            <option
                                                                                                key={
                                                                                                    option
                                                                                                }
                                                                                                value={
                                                                                                    option
                                                                                                }
                                                                                            >
                                                                                                {
                                                                                                    option
                                                                                                }
                                                                                            </option>
                                                                                        )
                                                                                    )}
                                                                                </select>
                                                                            )}
                                                                        </td>
                                                                    );
                                                                }
                                                            )}
                                                        </tr>
                                                    );
                                                }
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {!selectedBus && (
                        <div className="card">
                            <div className="card-body text-center text-muted">
                                <p>Select a bus type to configure</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function AddDeviceProfileWizard(props) {
    const handleFinish = async (emittedManifest) => {
        if (props.onFinish) {
            await props.onFinish(emittedManifest);
        }
    };

    return (
        <WizardView
            onClose={props.onClose}
            onFinish={handleFinish}
            title="Device Configurator"
            width="1100px"
            height="600px"
            showFinishPage={false}
        >
            <WizardView.Page
                title="Device Type"
                icon="microchip"
                onNextRequested={(e) => {
                    const selectedDevice =
                        e.detail.runtimeVariables?.selected_device;
                    /** validate moves here */
                    if (!selectedDevice) {
                        e.detail.prevented = true;
                    }
                }}
            >
                <DeviceTypePage />
            </WizardView.Page>

            <WizardView.Page title="Signals" icon="arrow-down">
                <SignalsPage />
            </WizardView.Page>

            <WizardView.Page title="Review" icon="check-circle">
                <FinishPage />
            </WizardView.Page>
        </WizardView>
    );
}

export default AddDeviceProfileWizard;
